import IdleTimer from 'exacta-material-react/lib/src/components/IdleTimer';
import { TargettedSystems } from 'nots-window-renderer/lib';
import QueryString from 'query-string';
import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import ConfigControl from '../../components/ConfigControl';
import System from '../../config/SystemConfig';
import {
    DisplayConstants, IdleTimeout, MonitorConfig, NotsSystem, S80Monitors, S80Tabs
} from '../../helpers/constants';
import { jsonToCSS } from '../../helpers/styles';
import { playerVersion } from '../../helpers/utils';
import useReportMonitorReady from '../../hooks/useReportMonitorReady';
import { listenInTab_AC, updateExecutionMode_AC } from '../../store/actions/executor.actions';
import { DemoInteractionBlocker } from '../common-components/DemoInteractionBlocker';
import GuidedContainer from '../common-components/GuidedContainer';
import useLogger from '../common-components/hooks/useLogger';
import ImageContainer from '../common-components/ImageContainer';
import Map from '../common-components/Map';
import { MultimediaInteractionBlocker } from '../common-components/MultimediaBlocker';
import { PauseOverlay } from '../common-components/PauseOverlay';
import PrimaryCloseControl from '../common-components/PrimaryCloseControl';
import TracksOnImage from '../common-components/TracksOnImage';
import WindowDisplayManager from '../common-components/WindowDisplayManager';
import MultimediaDialog from '../MultimediaDialog';
import AlertArea from './components/AlertArea';
import AudioInfoArea from './components/AudioInfoArea';
import AuxInfoArea from './components/AuxInfoArea';
import CommonInformationArea from './components/CommonInformationArea';
import CursorInfoArea from './components/CursorInfoArea';
import LinkStatusArea from './components/LinkStatusArea';
import RoleInfoArea from './components/RoleInfoArea';
import ShipInfoArea from './components/ShipInfoArea';
import SpareArea from './components/SpareArea';
import WorkModeArea from './components/WorkModeArea';
import LeftAnalogStrip from './LeftAnalogStrip';
import MonitorContext from './MonitorContext';
import RightAnalogStrip from './RightAnalogStrip';
import { colors, dimension } from './WindowConfig';

export interface ILowerMonitorProps {}

const MonitorContainer = styled.div`
  ${jsonToCSS(dimension.lowerMonitorSize)};
  background: ${colors.primary};
  color: ${colors.yellowText};
  display: flex;
  flex-direction: row;
`;

const UpperStrap = styled.div`
  ${jsonToCSS(dimension.upperStrap)}
  display: flex;
`;

const StyledDiv = styled.div<any>`
  ${({ width, height }) => `
  width: ${width};
  height: ${height};
  `};
  padding: 3px;
  border-right: solid 1px ${colors.border};
`;

const SizedDiv = styled.div<any>`
  ${({ width, height }) => `
  width: ${width};
  height: ${height};
  `};
`;

const GeneralDisplayArea = styled.div`
  ${jsonToCSS(dimension.generalDisplayArea)}
  background-color: ${colors.black};
  border-right: solid 1px ${colors.border};
  position: relative;
`;
const Body = styled.div`
  display: flex;
`;

const RightStrip = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;
`;

const CloseControlArea = styled.div`
  display: flex;
  flex-direction: row;
  ${jsonToCSS(dimension.closeControlArea)}
`;

const DataEntryArea = styled.div`
  padding: 0px;
  width: 400px;
  height: 640px;
  position: relative;
`;

const EachCloseControlArea = styled.div`
  display: flex;
  width: 50%;
  border-left: solid 1px ${colors.border};
  border-top: solid 1px ${colors.border};
  border-bottom: solid 1px ${colors.border};
  position: relative;
  > img {
    height: 20px;
    width: 20px;
    position: absolute;
    left: 50%;
    z-index: 20;
  }
  > hr {
    transform: rotate(90deg);
    height: 100px;
    position: absolute;
    top: -49px;
    left: 49px;
    opacity: 0.3;
  }
  > .rightHr {
    left: 156px;
    height: 87px;
    top: -42px;
  }
`;

export default function LowerMonitor(props: ILowerMonitorProps) {
  const { t } = useTranslation();
  const monitorConfig = useSelector(
    (state: any) => state.executor.s80.monitorLayout
  );
  const currentConfig = monitorConfig.lower;
  const { mode } = QueryString.parse(window.location.search);
  const { generalDisplayArea } = dimension;
  const generalDisplayAreaRef = useRef(null);
  const reportMonitorReady = useReportMonitorReady();
  useEffect(() => {
    System.setSystem(NotsSystem.S80).setCurrentTab(S80Tabs.UPPER);
  }, []);

  const dispatch = useDispatch();
  useEffect(() => {
    if (mode) {
      dispatch(updateExecutionMode_AC(`${mode}`));
    }
  }, [mode]);
  useEffect(() => {
    reportMonitorReady('lower');
    dispatch(listenInTab_AC());
  }, []);
  useLogger('LowerMonitor.tsx', 'Lower Monitor');

  return (
    <MonitorContainer {...dimension.lowerMonitorSize}>
      <MonitorContext.Provider
        value={{
          monitor: S80Monitors.LOWER,
        }}
      >
        <PauseOverlay />
        <DemoInteractionBlocker />
        <MultimediaInteractionBlocker />
        <IdleTimer timeout={IdleTimeout} logoutOnTimeout={false} />
        <GuidedContainer data-area="guided-container" />
        <LeftAnalogStrip></LeftAnalogStrip>
        <SizedDiv {...dimension.lowerMonitorDisplayArea}>
          <Helmet>
            <title>{`${t('appName')} - Lower Monitor ${playerVersion}`}</title>
          </Helmet>
          <UpperStrap>
            <StyledDiv {...dimension.sonar.roleInfoArea}>
              <RoleInfoArea monitor={DisplayConstants.LowerMonitor} />
            </StyledDiv>
            <ConfigControl
              elementFor={MonitorConfig.SONAR}
              config={currentConfig}
            >
              <StyledDiv {...dimension.sonar.workModeArea}>
                <WorkModeArea />
              </StyledDiv>
              <StyledDiv {...dimension.sonar.shipInfoArea}>
                <ShipInfoArea currentConfig={MonitorConfig.SONAR} />
              </StyledDiv>
              <StyledDiv {...dimension.sonar.cursorInfoArea}>
                <CursorInfoArea currentConfig={MonitorConfig.SONAR} />
              </StyledDiv>
              <StyledDiv {...dimension.sonar.audioInfoArea}>
                <AudioInfoArea />
              </StyledDiv>
              <StyledDiv {...dimension.sonar.auxInfoArea}>
                <AuxInfoArea currentConfig={MonitorConfig.SONAR} />
              </StyledDiv>
            </ConfigControl>
            <ConfigControl
              elementFor={MonitorConfig.SISTEMA}
              config={currentConfig}
            >
              <StyledDiv {...dimension.sistema.shipInfoArea}>
                <ShipInfoArea currentConfig={MonitorConfig.SISTEMA} />
              </StyledDiv>
              <StyledDiv {...dimension.sistema.lowerSpareArea}>
                <SpareArea currentConfig={MonitorConfig.SISTEMA} />
              </StyledDiv>
              <StyledDiv {...dimension.sistema.cursorInfoArea}>
                <CursorInfoArea currentConfig={MonitorConfig.SISTEMA} />
              </StyledDiv>
              <StyledDiv {...dimension.sistema.auxInfoArea}>
                <AuxInfoArea currentConfig={MonitorConfig.SISTEMA} />
              </StyledDiv>
            </ConfigControl>
            <ConfigControl
              elementFor={MonitorConfig.OBT}
              config={currentConfig}
            >
              <StyledDiv {...dimension.sistema.shipInfoArea}>
                <ShipInfoArea currentConfig={MonitorConfig.TACTICO} />
              </StyledDiv>
              <StyledDiv {...dimension.sistema.lowerSpareArea}>
                <SpareArea currentConfig={MonitorConfig.LINK} />
              </StyledDiv>
              <StyledDiv {...dimension.sistema.cursorInfoArea}>
                {/** This is empty for using an empty component SpareArea . Will change once we make dynamic*/}
                <SpareArea currentConfig={MonitorConfig.LINK} />{' '}
              </StyledDiv>
              <StyledDiv {...dimension.sistema.auxInfoArea}>
                <AuxInfoArea currentConfig={MonitorConfig.LINK} />
              </StyledDiv>
            </ConfigControl>
            <ConfigControl
              elementFor={MonitorConfig.TACTICO}
              config={currentConfig}
            >
              <StyledDiv {...dimension.tactico.shipInfoArea}>
                <ShipInfoArea currentConfig={MonitorConfig.TACTICO} />
              </StyledDiv>
              <StyledDiv {...dimension.tactico.spareArea}>
                <SpareArea currentConfig={MonitorConfig.TACTICO} />
              </StyledDiv>
              <StyledDiv {...dimension.tactico.auxInfoArea}>
                <AuxInfoArea currentConfig={MonitorConfig.TACTICO} />
              </StyledDiv>
            </ConfigControl>
            <ConfigControl
              elementFor={MonitorConfig.LINK}
              config={currentConfig}
            >
              <StyledDiv {...dimension.link.shipInfoArea}>
                <ShipInfoArea currentConfig={MonitorConfig.TACTICO} />
              </StyledDiv>
              <StyledDiv {...dimension.link.lowerSpareArea}>
                <SpareArea currentConfig={MonitorConfig.LINK} />
              </StyledDiv>
              <StyledDiv {...dimension.link.linkStatusArea}>
                <LinkStatusArea />
              </StyledDiv>
              <StyledDiv {...dimension.link.auxInfoArea}>
                <AuxInfoArea currentConfig={MonitorConfig.LINK} />
              </StyledDiv>
            </ConfigControl>
            <ConfigControl
              elementFor={MonitorConfig.WEAPON}
              config={currentConfig}
            >
              <StyledDiv {...dimension.weapon.shipInfoArea}>
                <ShipInfoArea currentConfig={MonitorConfig.WEAPON} />
              </StyledDiv>
            </ConfigControl>
            <ConfigControl
              elementFor={MonitorConfig.WPU}
              config={currentConfig}
            >
              <StyledDiv {...dimension.weapon.shipInfoArea}>
                <ShipInfoArea currentConfig={MonitorConfig.WEAPON} />
              </StyledDiv>
            </ConfigControl>
          </UpperStrap>
          <Body>
            <MultimediaDialog />
            <GeneralDisplayArea data-area="general-display-area">
              <Map
                monitor={DisplayConstants.LowerMonitor}
                svgHeight={generalDisplayArea.height}
                svgWidth={generalDisplayArea.width}
              />
              <CommonInformationArea
                areaRef={generalDisplayAreaRef}
                data-area="common-info-area"
              />
              <ImageContainer monitor={DisplayConstants.LowerMonitor} />
              <TracksOnImage />
              <WindowDisplayManager
                monitor={DisplayConstants.LowerMonitor}
                area={DisplayConstants.GeneralDisplayArea}
                targetSystem={TargettedSystems.S80}
              />
            </GeneralDisplayArea>
            <RightStrip>
              <CloseControlArea>
                <EachCloseControlArea>
                  <hr />
                  <hr className="rightHr" />
                  <img
                    src={require('../../assets/images/primaryHookIcon.png')}
                  />
                  <PrimaryCloseControl monitor={S80Monitors.LOWER} isPrimary />
                </EachCloseControlArea>
                <EachCloseControlArea>
                  <hr />
                  <hr className="rightHr" />
                  <img
                    src={require('../../assets/images/secondaryHookIcon.png')}
                  />
                  <PrimaryCloseControl
                    monitor={S80Monitors.LOWER}
                    isSecondary
                  />
                </EachCloseControlArea>
              </CloseControlArea>
              <DataEntryArea data-area="data-entry-area">
                <WindowDisplayManager
                  monitor={DisplayConstants.LowerMonitor}
                  area={DisplayConstants.DataEntryArea}
                  maxHeight={610}
                  targetSystem={TargettedSystems.S80}
                />
              </DataEntryArea>
              <StyledDiv {...dimension.alertArea}>
                <AlertArea></AlertArea>
              </StyledDiv>
            </RightStrip>
          </Body>
        </SizedDiv>
        <RightAnalogStrip></RightAnalogStrip>
      </MonitorContext.Provider>
    </MonitorContainer>
  );
}
